import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

export default function VoiceCommand({ onTranscript }) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [supported, setSupported] = useState(true);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSupported(false);
      return;
    }

    const rec = new SpeechRecognition();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = 'en-IN';

    rec.onresult = (e) => {
      const result = Array.from(e.results)
        .map((r) => r[0].transcript)
        .join('');
      setTranscript(result);
    };

    rec.onend = () => {
      setListening(false);
      if (transcript) {
        onTranscript?.(transcript);
        toast.success(`Voice captured: "${transcript.slice(0, 40)}..."`);
        setTranscript('');
      }
    };

    rec.onerror = (e) => {
      setListening(false);
      if (e.error !== 'no-speech') toast.error('Voice recognition error');
    };

    recognitionRef.current = rec;
  }, [onTranscript, transcript]);

  const toggleListening = useCallback(() => {
    if (!supported) { toast.error('Voice not supported in this browser'); return; }
    if (listening) {
      recognitionRef.current?.stop();
    } else {
      setTranscript('');
      recognitionRef.current?.start();
      setListening(true);
      toast('🎙️ Voice activated — speak your emergency', { icon: '🎤' });
    }
  }, [listening, supported]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.code === 'Space' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        toggleListening();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [toggleListening]);

  if (!supported) return null;

  return (
    <div className="relative">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleListening}
        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all border ${
          listening
            ? 'bg-crimson/20 border-crimson text-crimson shadow-glow-red'
            : 'bg-navy-card border-navy-border text-gray-400 hover:border-electric hover:text-electric'
        }`}
        aria-label={listening ? 'Stop voice recording' : 'Start voice command (Space)'}
        title="Hold Space or click to activate voice command"
      >
        {listening ? (
          <>
            <WaveformAnimation />
            <span>Listening...</span>
          </>
        ) : (
          <>
            <span>🎙️</span>
            <span>Voice</span>
            <span className="text-xs opacity-50 font-mono">Space</span>
          </>
        )}
      </motion.button>

      {/* Transcript preview */}
      <AnimatePresence>
        {transcript && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute bottom-full mb-2 left-0 right-0 glass-card p-2 text-xs text-gray-300 font-mono"
          >
            "{transcript}"
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function WaveformAnimation() {
  return (
    <div className="flex items-center gap-0.5 h-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="wave-bar w-0.5 bg-crimson rounded-full"
          style={{
            height: `${Math.random() * 12 + 4}px`,
            animationDelay: `${i * 0.1}s`,
          }}
        />
      ))}
    </div>
  );
}
